import { describePoint, minMax } from './Utils';
import { FrenzyColor } from './FrenzyConstants';
import { areSame } from './utils/Point';
import { describeLine } from './utils/Line';

export type FrenzyAxisLabel = 'HORIZONTAL' | 'VERTICAL';

export enum DrawMode {
  NOT_DRAWING = 0,
  SLOW = 1,
  FAST = 2
};

export type Point = [number, number];

export type FrenzyLine = {
	direction: FrenzyDirection;
	start: Point;
	end: Point;
};

export type FrenzyAxis = {
  label: FrenzyAxisLabel;
  oppositeLabel: FrenzyAxisLabel;
  primaryPositionOfInterest: (x: number, y:number) => number;
  secondaryPositionOfInterest: (x: number, y:number) => number;
  secondaryMinMax: (line: FrenzyLine) => [number, number];
  intersects: (x:number, y:number, line: FrenzyLine) => boolean;
  expressPrimarySecondaryAsXY: (x: number, y:number) => number[];
};

export const Axis:{HORIZONTAL: FrenzyAxis, VERTICAL: FrenzyAxis} = {
  HORIZONTAL: {
    label: 'HORIZONTAL',
    oppositeLabel: 'VERTICAL',
    primaryPositionOfInterest: (x, y) => y,
    secondaryPositionOfInterest: (x, y) => x,
    secondaryMinMax: (line) => minMax(line.start[0], line.end[0]),
    intersects: (x, y, line) => {
      const [lowestX, highestX] = minMax(line.start[0], line.end[0]);
      //      console.log(`checking HORIZONTAL intersect of ${lowestX}, ${x}, ${highestX} on Y axis ${y}`);
      return y === line.start[1] && (lowestX <= x) && (x <= highestX);
    },
    expressPrimarySecondaryAsXY: (p, s) => [s, p]
  },
  VERTICAL: {
    label: 'VERTICAL',
    oppositeLabel: 'HORIZONTAL',
    primaryPositionOfInterest: (x, y) => x,
    secondaryPositionOfInterest: (x, y) => y,
    secondaryMinMax: (line) => minMax(line.start[1], line.end[1]),
    intersects: (x, y, line) => {
      const [lowestY, highestY] = minMax(line.start[1], line.end[1]);
      //     console.log(`checking VERT intersect of ${lowestY}, ${y}, ${highestY} on x axis ${x}`);

      return x === line.start[0] && (lowestY <= y) && (y <= highestY);
    },
    expressPrimarySecondaryAsXY: (p, s) => [p, s]
  }
};


export type FrenzyXYTuple = {
  x: number;
  y: number;
};

export type DiagonalDirectionLabel = 'SE' | 'SW' | 'NW' | 'NE';

type ReflectionDescriptor = {
	axis: FrenzyAxisLabel;
	result: DiagonalDirectionLabel;
};

export function diagonalDirectionForLabel(label: DiagonalDirectionLabel):DiagonalDirection {
  return LeptonDirection[label];
};


export type DiagonalDirection = {
  label: DiagonalDirectionLabel;
  positionModifier: (xy: FrenzyXYTuple, speed: number) => FrenzyXYTuple;
  reflection: (lineAxis: FrenzyAxisLabel) => DiagonalDirectionLabel;
  opposite: DiagonalDirectionLabel,
  // returns true if the given line is irrelevant to bounce calculations
  irrelevantBounceLineFilter: (xyPoint: Point) => (line: FrenzyLine) => boolean
};

export const LeptonDirection: { SE: DiagonalDirection, SW: DiagonalDirection, NW: DiagonalDirection, NE: DiagonalDirection } = {
	SE: {
    label: 'SE',
    positionModifier: (xy: FrenzyXYTuple, speed: number) => ({ x: xy.x + speed, y: xy.y + speed }),
    reflection: (lineAxis: FrenzyAxisLabel) => lineAxis === 'VERTICAL' ? 'SW' : 'NE' ,
    opposite: 'NW',
    // if you're arriving SE and you hit:
    //
    // --------------------x-------------
    //                     |
    // the southbound line doesn't matter 
    // because it doesn't poke into the 
    // Lepton space.
    // So lines in direction DOWN with a start at x
    // AND lines in direction UP with an end at x
    // should be considered irrelevant.
    /*
     * But if arriving SE at:         |
     *                                |
     *                                x--
     *                                |
     * 
     * Then it's the RIGHT line that doesn't matter. WTF? 
     */
    irrelevantBounceLineFilter: (xyPoint: Point) => (line: FrenzyLine) => {
      return (line.direction.label === 'DOWN' && areSame(line.start, xyPoint)) ||
      (line.direction.label === 'UP' && areSame(line.end, xyPoint)) ||
      (line.direction.label === 'RIGHT' && areSame(line.start, xyPoint)) ||
      (line.direction.label === 'LEFT' && areSame(line.end, xyPoint))
    }
	},
	SW: {
    label: 'SW',
    positionModifier: (xy: FrenzyXYTuple, speed: number) => ({ x: xy.x - speed, y: xy.y + speed }),
		reflection: (lineAxis: FrenzyAxisLabel) => lineAxis === 'VERTICAL' ? 'SE': 'NW',
    opposite: 'NE',
    irrelevantBounceLineFilter: (xyPoint: Point) => (line: FrenzyLine) => {
      // console.log(`SW check of ${describePoint(xyPoint)} against ${describeLine(line)} => ${(line.direction.label === 'DOWN' && areSame(line.start, xyPoint)) ||
      // (line.direction.label === 'UP' && areSame(line.end, xyPoint)) ||
      // (line.direction.label === 'LEFT' && areSame(line.start, xyPoint)) ||
      // (line.direction.label === 'RIGHT' && areSame(line.end, xyPoint))}`)
      return (line.direction.label === 'DOWN' && areSame(line.start, xyPoint)) ||
      (line.direction.label === 'UP' && areSame(line.end, xyPoint)) ||
      (line.direction.label === 'LEFT' && areSame(line.start, xyPoint)) ||
      (line.direction.label === 'RIGHT' && areSame(line.end, xyPoint))
    }
	},	
	NW: {
    label: 'NW',
    positionModifier: (xy: FrenzyXYTuple, speed: number) => ({ x: xy.x - speed, y: xy.y - speed }),
		reflection: (lineAxis: FrenzyAxisLabel) => lineAxis === 'VERTICAL' ? 'NE':'SW',
    opposite: 'SE',
    irrelevantBounceLineFilter: (xyPoint: Point) => (line: FrenzyLine) => {
      return (line.direction.label === 'UP' && areSame(line.start, xyPoint)) ||
      (line.direction.label === 'DOWN' && areSame(line.end, xyPoint)) ||
      (line.direction.label === 'LEFT' && areSame(line.start, xyPoint)) ||
      (line.direction.label === 'RIGHT' && areSame(line.end, xyPoint))
    }
	},
	NE: {
    label: 'NE',
    positionModifier: (xy: FrenzyXYTuple, speed: number) => ({ x: xy.x + speed, y: xy.y - speed }),
		reflection: (lineAxis: FrenzyAxisLabel) => lineAxis === 'VERTICAL' ? 'NW':'SE',
    opposite: 'SW',
    irrelevantBounceLineFilter: (xyPoint: Point) => (line: FrenzyLine) => {
      return (line.direction.label === 'UP' && areSame(line.start, xyPoint)) ||
      (line.direction.label === 'DOWN' && areSame(line.end, xyPoint)) ||
      (line.direction.label === 'RIGHT' && areSame(line.start, xyPoint)) ||
      (line.direction.label === 'LEFT' && areSame(line.end, xyPoint))
    }
	}
};

export type LeptonState = {
  leptonId: number;
	x: number;
  y: number;
	direction: DiagonalDirection
};

export type FrenzyPlayerPosition = {
  playerX: number;
  playerY: number;
};

export type FrenzyDirectionLabel = 'LEFT' | 'RIGHT' | 'UP' | 'DOWN';

export type FrenzyDirection = {
  axis: FrenzyAxis;
  oppositeAxis: FrenzyAxis;
  label: FrenzyDirectionLabel;
  headingDegrees: number;
  oppositeLabel: FrenzyDirectionLabel;
  lineToPrimaryPoi: (line: FrenzyLine) => number;
  canMoveOnLine: (x: number, y: number, line: FrenzyLine) => boolean;
  traverse: (x: number, y:number, amount?: number) => FrenzyXYTuple;
  traverseFromPoint: (xy: Point, amount?: number) => Point;
  reverseFromPoint: (xy: Point, amount?: number) => Point;
  positionModifier: (fpp: FrenzyPlayerPosition, drawMode: DrawMode) => FrenzyPlayerPosition;
}


enum Op {
  PLUS = 1,
  MINUS = -1
}


const modifyPosition = (startValue: number, drawMode: DrawMode, op: Op): number => {
  switch (drawMode) {
    case DrawMode.SLOW:
      return startValue + (op * 1);
    case DrawMode.FAST:
    default: 
      return startValue + op;
  }
}

export const Direction: { LEFT: FrenzyDirection, RIGHT: FrenzyDirection, UP: FrenzyDirection, DOWN: FrenzyDirection } = {
  LEFT: {
    axis: Axis.HORIZONTAL,
    oppositeAxis: Axis.VERTICAL,
    label: 'LEFT',
    headingDegrees: 270,
    oppositeLabel: 'RIGHT',
    lineToPrimaryPoi: (line: FrenzyLine) => line.start[1],
    canMoveOnLine: (x: number, y: number, line: FrenzyLine) => x > Math.min(line.start[0], line.end[0]),
    traverse: (x: number, y: number, amount:number = 1) => ({ x: (x - amount), y }),
    traverseFromPoint: (xy: Point, amount:number = 1) => ([xy[0] - amount, xy[1]]),
    reverseFromPoint: (xy: Point, amount:number = 1) => ([xy[0] + amount, xy[1]]),
    positionModifier: (fpp: FrenzyPlayerPosition, drawMode: DrawMode) => ({playerX: modifyPosition(fpp.playerX, drawMode, Op.MINUS), playerY: fpp.playerY})
  },
  RIGHT: {
    axis: Axis.HORIZONTAL,
    oppositeAxis: Axis.VERTICAL,
    label: 'RIGHT',
    headingDegrees: 90,
    oppositeLabel: 'LEFT',
    lineToPrimaryPoi: (line: FrenzyLine) => line.start[1],
    canMoveOnLine: (x: number, y: number, line: FrenzyLine) => x < Math.max(line.start[0], line.end[0]),
    traverse: (x: number, y: number, amount:number = 1) => ({ x: (x + amount), y }),
    traverseFromPoint: (xy: Point, amount:number = 1) => ([xy[0] + amount, xy[1]]),
    reverseFromPoint: (xy: Point, amount:number = 1) => ([xy[0] - amount, xy[1]]),
    positionModifier: (fpp: FrenzyPlayerPosition, drawMode: DrawMode) => ({playerX: modifyPosition(fpp.playerX, drawMode, Op.PLUS), playerY: fpp.playerY})
  },
  UP: {
    axis: Axis.VERTICAL,
    oppositeAxis: Axis.HORIZONTAL,
    label: 'UP',
    headingDegrees: 0,
    oppositeLabel: 'DOWN',
    lineToPrimaryPoi: (line: FrenzyLine) => line.start[0],
    canMoveOnLine: (x: number, y: number, line: FrenzyLine) => y > Math.min(line.start[1], line.end[1]),
    traverse: (x: number, y: number, amount:number = 1) => ({ x, y: (y - amount) }),
    traverseFromPoint: (xy: Point, amount:number = 1) => ([xy[0], xy[1]- amount]),
    reverseFromPoint: (xy: Point, amount:number = 1) => ([xy[0], xy[1] + amount]),
    positionModifier: (fpp: FrenzyPlayerPosition, drawMode: DrawMode) => ({playerY: modifyPosition(fpp.playerY, drawMode, Op.MINUS), playerX: fpp.playerX})
  },
  DOWN: {
    axis: Axis.VERTICAL,
    oppositeAxis: Axis.HORIZONTAL,
    label: 'DOWN',
    headingDegrees: 180,
    oppositeLabel: 'UP',
    lineToPrimaryPoi: (line: FrenzyLine) => line.start[0],
    canMoveOnLine: (x: number, y: number, line: FrenzyLine) => y < Math.max(line.start[1], line.end[1]),
    traverse: (x: number, y: number, amount:number = 1) => ({ x, y: (y + amount) }),
    traverseFromPoint: (xy: Point, amount:number = 1) => ([xy[0], xy[1] + amount]),
    reverseFromPoint: (xy: Point, amount:number = 1) => ([xy[0], xy[1] - amount]),
    positionModifier: (fpp: FrenzyPlayerPosition, drawMode: DrawMode) => ({playerY: modifyPosition(fpp.playerY, drawMode, Op.PLUS), playerX: fpp.playerX})
  },
};

export const directionForLabel = (label: FrenzyDirectionLabel) => {
  return Direction[label];
};

export type FrenzyRotationLabel = 'CLOCKWISE' | 'COUNTER-CLOCKWISE';

export type FrenzyRotation = {
  label: FrenzyRotationLabel;
  appliedTo: (direction: FrenzyDirection) => FrenzyDirection;
}

export const Rotation: { CCW: FrenzyRotation, CW: FrenzyRotation } = {
  CCW: {
    label: 'COUNTER-CLOCKWISE',
    appliedTo: (direction: FrenzyDirection) => {
      const newHeading = ((direction.headingDegrees + 360) - 90) % 360;
      return Object.values(Direction).find(d => d.headingDegrees === newHeading) || Direction.UP;
    }
  },
  CW: {
    label: 'CLOCKWISE',
    appliedTo: (direction: FrenzyDirection) => {
      const newHeading = (direction.headingDegrees + 90) % 360;
      return Object.values(Direction).find(d => d.headingDegrees === newHeading) || Direction.UP;
    }
  }
};

export type FrenzyPolygon = {
  polygonLines: FrenzyLine[]
  area: number
  innerArea: number
};

export type FrenzyFilledPolygon = FrenzyPolygon & {
  drawMode: DrawMode
  color: FrenzyColor
};

export type SparseArray = {
  width: number,
  height: number,
  bits: boolean[]
}

export type PixelField = {
  sparseArray: SparseArray
  newFrontier: Set<Point>
  color: FrenzyColor
}

export type Level ={
  number: number;
  bonusMultiplier: number;
  leptonCount: number;
  leptonSpeed: number;
  chaserCount: number;
  chaserSpeed: number;
}

export const defaultLevel = {
  number: 1,
  bonusMultiplier: 1,
  leptonCount: 1,
  leptonSpeed: 1,
  chaserCount: 0,
  chaserSpeed: 1
};

export const createLevel = (levelNumber:number) => {
  const l = defaultLevel;

  l.number = levelNumber;
  l.bonusMultiplier = levelNumber;

  // Leptons; level 1-3 = 1, levels 4-6 = 2 etc
  l.leptonCount = Math.floor((levelNumber - 1) / 3) + 1;
  // Lepton speed = level % 3 -> slow, medium, fast
  l.leptonSpeed =  Math.floor((levelNumber - 1) % 3) + 1;

  return l;
};
